/**
 * Filtros de inspeção do comparador (E26.F4/ADR-0039). // ux-neutrality:allow doc-comment
 *
 * Client-side puro, apresentação-only, estado efêmero (ADR-0039 §A): // ux-neutrality:allow doc-comment
 *  - inverter: lente global — CSS inverte TODAS as imagens do modo (§B) // ux-neutrality:allow doc-comment
 *  - grade: referência visual SEM medição, px de tela, sem rótulos (§C) // ux-neutrality:allow doc-comment
 *  - régua: segmento por arraste DENTRO de uma imagem; comprimento em px da // ux-neutrality:allow doc-comment
 *    imagem original (fator de escala do object-contain), nota ADR-0013 (§D) // ux-neutrality:allow doc-comment
 *
 * Módulo extraído do ContestedCanvasTools por max-lines (lição E26.F1 / // ux-neutrality:allow doc-comment
 * padrão InstrumentPanels). // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';

export interface InspectionFiltersState {
  invert: boolean;
  grid: boolean;
  ruler: boolean;
}

export const INSPECTION_FILTERS_OFF: InspectionFiltersState = {
  invert: false,
  grid: false,
  ruler: false,
};

/**
 * Comprimento de um segmento de tela em px da IMAGEM original (ADR-0039 §D).
 *
 * `object-contain` aplica escala uniforme `s = min(boxW/natW, boxH/natH)`
 * (letterboxing não afeta comprimento — só offsets). Dimensões indisponíveis
 * (jsdom, imagem ainda não carregada) ⇒ fallback honesto: px de tela.
 */
export function imageSegmentLengthPx(args: {
  dx: number;
  dy: number;
  boxW: number;
  boxH: number;
  naturalW: number;
  naturalH: number;
}): number {
  const screenLength = Math.hypot(args.dx, args.dy);
  const scale = Math.min(args.boxW / args.naturalW, args.boxH / args.naturalH);
  if (!Number.isFinite(scale) || scale <= 0) return Math.round(screenLength);
  return Math.round(screenLength / scale);
}

export interface InspectionFiltersToolbarProps {
  filters: InspectionFiltersState;
  onToggle: (key: keyof InspectionFiltersState) => void;
  /** Régua só tem leitura honesta no modo Comparar (uma imagem por painel —
   * ADR-0039 §D); nos modos sobrepostos as duas imagens dividem o mesmo box
   * e a medida seria ambígua entre capturas de escalas diferentes. */
  rulerAvailable: boolean;
}

export function InspectionFiltersToolbar({
  filters,
  onToggle,
  rulerAvailable,
}: InspectionFiltersToolbarProps): JSX.Element {
  return (
    <>
      <span className="inspection-divider" aria-hidden="true" />
      <button
        type="button"
        data-testid="inspect-invert"
        aria-pressed={filters.invert}
        onClick={() => onToggle('invert')}
      >
        {ptBR.workspaceCaso.inspectInvert}
      </button>
      <button
        type="button"
        data-testid="inspect-grid"
        aria-pressed={filters.grid}
        onClick={() => onToggle('grid')}
      >
        {ptBR.workspaceCaso.inspectGrid}
      </button>
      <button
        type="button"
        data-testid="inspect-ruler"
        aria-pressed={rulerAvailable && filters.ruler}
        disabled={!rulerAvailable}
        title={rulerAvailable ? undefined : ptBR.workspaceCaso.inspectRulerCompareOnly}
        onClick={() => onToggle('ruler')}
      >
        {ptBR.workspaceCaso.inspectRuler}
      </button>
    </>
  );
}

/** Grade de referência por cima das imagens (ADR-0039 §C): px de tela, sem
 * rótulos numéricos, sem alegação de escala entre K e Q. */
export function GridOverlay(): JSX.Element {
  return <div className="inspection-grid" data-testid="inspection-grid" aria-hidden="true" />;
}

interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Arraste mínimo (px de TELA) para uma medição valer (E27.F1.C02 /
 * F-E27.F1-02): abaixo disso é clique acidental — o `mousedown` já cria um
 * segmento degenerado (início == fim) e, sem o limiar, o `mouseup` disparava
 * `onMeasured` (escondendo o hint do gesto) e exibia `≈ 0 px`.
 */
export const RULER_MIN_DRAG_SCREEN_PX = 5;

interface RulerLayerProps {
  imgRef: React.RefObject<HTMLImageElement>;
  /** E27.F1: primeira medição concluída — o pai esconde o hint do gesto. */
  onMeasured?: (() => void) | undefined;
}

function RulerLayer({ imgRef, onMeasured }: RulerLayerProps): JSX.Element {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [segment, setSegment] = useState<Segment | null>(null);
  const [dragging, setDragging] = useState(false);

  // Esc limpa o segmento (toggle off também — o layer desmonta).
  useEffect(() => {
    if (!segment) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSegment(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [segment]);

  /**
   * E36.F2: fator de render do ancestral com CSS transform (ZoomPanViewport).
   * `getBoundingClientRect` devolve px de TELA (pós-transform); `offsetWidth`
   * devolve px de LAYOUT (pré-transform). A razão é o zoom efetivo — dividir
   * por ela mantém o segmento em px de layout, então a régua mede IGUAL com e
   * sem zoom (defensibilidade: zoom nunca vira medida).
   */
  function renderFactor(): number {
    const el = surfaceRef.current;
    if (!el || !el.offsetWidth) return 1;
    const f = el.getBoundingClientRect().width / el.offsetWidth;
    return Number.isFinite(f) && f > 0 ? f : 1;
  }

  function toLocal(event: React.MouseEvent): { x: number; y: number } {
    const rect = surfaceRef.current?.getBoundingClientRect();
    const f = renderFactor();
    return {
      x: (event.clientX - (rect?.left ?? 0)) / f,
      y: (event.clientY - (rect?.top ?? 0)) / f,
    };
  }

  function measuredLength(current: Segment): number {
    const img = imgRef.current;
    const el = surfaceRef.current;
    return imageSegmentLengthPx({
      dx: current.x2 - current.x1,
      dy: current.y2 - current.y1,
      boxW: el?.offsetWidth ?? 0,
      boxH: el?.offsetHeight ?? 0,
      naturalW: img?.naturalWidth ?? 0,
      naturalH: img?.naturalHeight ?? 0,
    });
  }

  return (
    <div className="ruler-layer" data-testid="ruler-layer">
      {/* Superfície de desenho por arraste — não há role interativa semântica
          para "medir com o mouse"; Esc (local + global) limpa o segmento.
          Mesmo padrão de disable pontual justificado do LRMeter/WizardStepper. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        ref={surfaceRef}
        className="ruler-surface"
        data-testid="ruler-surface"
        role="application"
        aria-label={ptBR.workspaceCaso.inspectRuler}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- foco p/ Esc limpar a medição
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setSegment(null);
        }}
        onMouseDown={(event) => {
          // E36.F2: só botão esquerdo mede — botão do meio é pan do viewport.
          if (event.button !== 0) return;
          const p = toLocal(event);
          setSegment({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
          setDragging(true);
        }}
        onMouseMove={(event) => {
          if (!dragging || !segment) return;
          const p = toLocal(event);
          setSegment({ ...segment, x2: p.x, y2: p.y });
        }}
        onMouseUp={() => {
          setDragging(false);
          if (!segment) return;
          const screenLength = Math.hypot(segment.x2 - segment.x1, segment.y2 - segment.y1);
          if (screenLength < RULER_MIN_DRAG_SCREEN_PX) {
            // Clique sem arraste: nada a medir — descarta o segmento e mantém
            // o hint (não chama onMeasured).
            setSegment(null);
            return;
          }
          onMeasured?.();
        }}
        onMouseLeave={() => setDragging(false)}
      >
        {segment && (
          <svg className="ruler-svg" aria-hidden="true">
            <line x1={segment.x1} y1={segment.y1} x2={segment.x2} y2={segment.y2} />
            <circle cx={segment.x1} cy={segment.y1} r={3} />
            <circle cx={segment.x2} cy={segment.y2} r={3} />
          </svg>
        )}
        {segment && !dragging && (
          <span
            className="ruler-measure"
            data-testid="ruler-measure"
            style={{
              left: (segment.x1 + segment.x2) / 2,
              top: (segment.y1 + segment.y2) / 2,
            }}
          >
            {`≈ ${measuredLength(segment)} ${ptBR.workspaceCaso.inspectRulerUnit}`}
          </span>
        )}
      </div>
    </div>
  );
}

export interface MeasurableImageProps {
  src: string;
  alt: string;
  className: string;
  rulerActive: boolean;
  /** E27.F1: repassado à RulerLayer — primeira medição esconde o hint do gesto. */
  onRulerMeasured?: (() => void) | undefined;
}

/** Imagem com camada de régua opcional (ADR-0039 §D — a medição vive DENTRO
 * de uma imagem; cada painel mede a sua, nunca entre capturas). */
export function MeasurableImage({
  src,
  alt,
  className,
  rulerActive,
  onRulerMeasured,
}: MeasurableImageProps): JSX.Element {
  const imgRef = useRef<HTMLImageElement>(null);
  return (
    <div className="measurable-img">
      <img ref={imgRef} src={src} alt={alt} className={className} />
      {rulerActive && <RulerLayer imgRef={imgRef} onMeasured={onRulerMeasured} />}
    </div>
  );
}
